import { useEffect, useRef, useState } from "react";
import { Linkedin, Twitter, MessageCircle, Mail, Link2, Volume2, Pause, Play, Square, Check } from "lucide-react";
import { toast } from "sonner";

const stripHtml = (html) => {
  if (typeof window === "undefined") return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
};

const ShareBar = ({ post, articleRef }) => {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const title = post.title;
  const excerpt = post.excerpt;
  const tags = post.tags || [];
  const [copied, setCopied] = useState(false);

  // ── Read aloud ──
  const [speechState, setSpeechState] = useState("idle"); // idle | playing | paused
  const [speechSupported, setSpeechSupported] = useState(false);
  const utteranceRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      setSpeechSupported(true);
    }
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const startSpeech = () => {
    if (!speechSupported) return;
    window.speechSynthesis.cancel();
    const text = articleRef?.current
      ? stripHtml(articleRef.current.innerHTML)
      : `${title}. ${stripHtml(post.content || "")}`;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 1;
    u.onend = () => setSpeechState("idle");
    u.onerror = () => setSpeechState("idle");
    utteranceRef.current = u;
    window.speechSynthesis.speak(u);
    setSpeechState("playing");
  };

  const pauseSpeech = () => {
    if (!speechSupported) return;
    window.speechSynthesis.pause();
    setSpeechState("paused");
  };
  const resumeSpeech = () => {
    if (!speechSupported) return;
    window.speechSynthesis.resume();
    setSpeechState("playing");
  };
  const stopSpeech = () => {
    if (!speechSupported) return;
    window.speechSynthesis.cancel();
    setSpeechState("idle");
  };

  // ── Share links ──
  const enc = encodeURIComponent;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}&title=${enc(title)}&summary=${enc(excerpt)}`;
  const twitterHref = `https://twitter.com/intent/tweet?text=${enc(`${title} via @Spiruva ${url} ${tags.slice(0, 2).map((t) => `#${t.replace(/\s+/g, "")}`).join(" ")}`)}`;
  const whatsappHref = `https://wa.me/?text=${enc(`${title}\n\n${excerpt}\n\nRead more: ${url}`)}`;
  const mailHref = `mailto:?subject=${enc(`${title} — Spiruva Industry Insights`)}&body=${enc(`I thought you'd find this useful:\n\n${title}\n${excerpt}\n\nFull article: ${url}`)}`;

  const copyLink = async () => {
    const writeFallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch { return false; }
    };
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch { /* fall through */ }
    if (!ok) ok = writeFallback();
    if (ok) {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Could not copy link");
    }
  };

  const SHARE_BUTTONS = [
    { id: "linkedin", href: linkedinHref, icon: Linkedin, label: "Share on LinkedIn" },
    { id: "twitter", href: twitterHref, icon: Twitter, label: "Share on X / Twitter" },
    { id: "whatsapp", href: whatsappHref, icon: MessageCircle, label: "Share on WhatsApp" },
    { id: "email", href: mailHref, icon: Mail, label: "Share via Email" },
  ];

  // Desktop: vertical left rail. Mobile: bottom sticky.
  return (
    <>
      {/* Desktop vertical rail */}
      <aside
        data-testid="share-bar-desktop"
        className="hidden lg:flex fixed left-6 xl:left-10 top-1/2 -translate-y-1/2 z-30 flex-col gap-2 bg-white border border-brand-green/15 p-2 shadow-sm"
      >
        {SHARE_BUTTONS.map((b) => (
          <a
            key={b.id}
            href={b.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={b.label}
            data-testid={`share-${b.id}`}
            className="w-10 h-10 flex items-center justify-center text-brand-black/70 hover:bg-brand-cream hover:text-brand-teal transition"
          >
            <b.icon size={16} />
          </a>
        ))}
        <button
          onClick={copyLink}
          aria-label="Copy article link"
          data-testid="share-copy"
          className="w-10 h-10 flex items-center justify-center text-brand-black/70 hover:bg-brand-cream hover:text-brand-teal transition"
        >
          {copied ? <Check size={16} className="text-brand-green" /> : <Link2 size={16} />}
        </button>
        {speechSupported && (
          <div className="border-t border-brand-green/15 mt-1 pt-1 flex flex-col gap-1">
            {speechState === "idle" && (
              <button onClick={startSpeech} aria-pressed="false" aria-label="Read article aloud" data-testid="speech-play" className="w-10 h-10 flex items-center justify-center text-brand-black/70 hover:bg-brand-cream hover:text-brand-teal transition">
                <Volume2 size={16} />
              </button>
            )}
            {speechState === "playing" && (
              <>
                <button onClick={pauseSpeech} aria-pressed="true" aria-label="Pause reading" data-testid="speech-pause" className="w-10 h-10 flex items-center justify-center text-brand-teal bg-brand-teal/10">
                  <Pause size={16} />
                </button>
                <button onClick={stopSpeech} aria-label="Stop reading" data-testid="speech-stop" className="w-10 h-10 flex items-center justify-center text-brand-black/70 hover:bg-brand-cream hover:text-brand-teal transition">
                  <Square size={14} />
                </button>
              </>
            )}
            {speechState === "paused" && (
              <>
                <button onClick={resumeSpeech} aria-pressed="false" aria-label="Resume reading" data-testid="speech-resume" className="w-10 h-10 flex items-center justify-center text-brand-teal bg-brand-teal/10">
                  <Play size={16} />
                </button>
                <button onClick={stopSpeech} aria-label="Stop reading" data-testid="speech-stop-2" className="w-10 h-10 flex items-center justify-center text-brand-black/70 hover:bg-brand-cream hover:text-brand-teal transition">
                  <Square size={14} />
                </button>
              </>
            )}
          </div>
        )}
      </aside>

      {/* Mobile bottom bar */}
      <div
        data-testid="share-bar-mobile"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-brand-green/15 flex items-center justify-around p-2"
      >
        {SHARE_BUTTONS.map((b) => (
          <a
            key={b.id}
            href={b.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={b.label}
            className="w-11 h-11 flex items-center justify-center text-brand-black/70"
          >
            <b.icon size={18} />
          </a>
        ))}
        <button onClick={copyLink} aria-label="Copy link" className="w-11 h-11 flex items-center justify-center text-brand-black/70">
          {copied ? <Check size={18} className="text-brand-green" /> : <Link2 size={18} />}
        </button>
        {speechSupported && (
          <button
            onClick={speechState === "idle" ? startSpeech : speechState === "playing" ? pauseSpeech : resumeSpeech}
            aria-pressed={speechState === "playing"}
            aria-label="Read article aloud"
            className="w-11 h-11 flex items-center justify-center text-brand-teal"
          >
            {speechState === "playing" ? <Pause size={18} /> : <Volume2 size={18} />}
          </button>
        )}
      </div>
    </>
  );
};

export default ShareBar;
